<script setup lang="ts">
import { ref, type Ref, reactive, computed, watch, onMounted } from 'vue'
import { Briefcase, User, Plus, Bold, Italic, List, ChevronDown, FolderOpen, Search, Check } from 'lucide-vue-next'
import GoogleMap from '@/components/marketplace/generic/GoogleMap.vue'
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/vue'
import { useTalentPoolsStore } from '@/stores/talentPoolsStore'
import { useProjectsStore } from '@/stores/projectsStore'
import { Button } from '@/components/marketplace/ui'
import { useEditor, EditorContent, type Editor } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import VueDatePicker from "@vuepic/vue-datepicker"
import "@vuepic/vue-datepicker/dist/main.css"; // import styles
import type { Project, NewProject } from '@/types/projects'
import type { TalentPool } from '@/types/talentPools'
import { useDropdownFloating } from '@/composables/useDropdownFloating'


const props = defineProps<{
  initialData?: Partial<TalentPool> | null
  mode?: 'create' | 'edit'
}>()

// ==============================
// 1. Types
// ==============================
const workTypes = [
  { value: 'contract', label: 'Contract' },
  { value: 'permanent', label: 'Permanent' },
  { value: 'either', label: 'Either' }
] as const

const salaryTypes = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'annually', label: 'Annually' }
] as const

const hiringTimelineOptions = [
  { value: 'immediate', label: 'Immediate' },
  { value: 'within_1_month', label: 'Within 1 month' },
  { value: 'ongoing', label: 'Casual Pool / Ongoing' },
  { value: 'months_1_3', label: '1 to 3 months' },
  { value: 'months_3_6', label: '3 to 6 months' },
  { value: 'specific_date', label: 'Specific date' }
] as const

const projectDurations = [
  '1-2 weeks',
  '1 month',
  '2-3 months',
  '3-6 months',
  '6-12 months',
  '12+ months'
] as const

type ProjectMode = 'existing' | 'new'
type TalentPoolType = 'general' | 'project' | null
type WorkType = typeof workTypes[number]['value']
type SalaryType = typeof salaryTypes[number]['value']
type HiringTimeline = typeof hiringTimelineOptions[number]['value'] | null
type ProjectDuration = typeof projectDurations[number]


interface Location {
  address: string
  lat: number | null
  lng: number | null
}

interface TalentPoolForm {
  type: TalentPoolType
  location: Location
  title: string
  description: string | null
  workType: WorkType
  salary: number | null
  salaryType: SalaryType
  hiringTimeline: HiringTimeline
  timeline: string | null
  requirements: string[]
  qualifications: string[]
  project: Project | NewProject | null
}

interface Step {
  id: number
  key: 'basicInfo' | 'jobDetails' | 'projectDetails' | 'review'
  label: string
}

// ==============================
// 2. Helpers
// ==============================
const createEmptyProject = (): NewProject => ({
  name: '',
  summary: '',
  budget: null,
  startDate: null,
  length: projectDurations[0]
})

const createEmptyForm = (): TalentPoolForm => ({
  type: null,
  location: {
    address: '',
    lat: null,
    lng: null
  },
  title: '',
  description: null,
  workType: 'contract',
  salary: null,
  salaryType: 'annually',
  hiringTimeline: null,
  timeline: null,
  requirements: [],
  qualifications: [],
  project: null
})

const isLocationInvalid = ref(false)
const talentPoolsStore = useTalentPoolsStore();
const projectsStore = useProjectsStore();
const projectMode = ref<ProjectMode>('existing')
const selectedProject = ref<Project | null>(null)


// ==============================
// 3. State
// ==============================
const contentAreaRef = ref<HTMLElement | null>(null)
const poolRequirement = ref('')
const poolQualification = ref('')

const form = reactive<TalentPoolForm>(createEmptyForm())
const newProject = ref<NewProject>(createEmptyProject())

// keep project in sync with type
watch(() => form.type, (t) => {
  if (t === 'project' && !form.project) {
    form.project = createEmptyProject()
  }
  if (t !== 'project') {
    form.project = null
  }
})

const editor = useEditor({
  content: '', // We'll start with it empty
  extensions: [
    StarterKit,
  ],
  editorProps: {
    attributes: {
      class: 'min-h-[150px] resize-y overflow-auto w-full p-3 focus:outline-none !bg-[#e5e5e54d] text-[#0a0a0a] rounded-b-md',
    },
  },
})

watch(
  () => props.initialData,
  (data) => {
    if (!data) return

    // ==========================
    // 1. Map basic fields
    // ==========================
    Object.assign(form, {
      type: data.type ?? form.type,
      title: data.title ?? form.title,
      location: {
        address: data.location ?? form.location.address,
        lat: data.location_lat ?? form.location.lat,
        lng: data.location_lng ?? form.location.lng,
      },
      workType: data.workType ?? form.workType,
      salary: data.rate ?? form.salary,
      salaryType: data.rateType ?? form.salaryType,
      hiringTimeline: (data as any).hiring_timeline ?? (data.timeline ? 'specific_date' : form.hiringTimeline),
      timeline: data.timeline ?? (data as any).timeline_date ?? form.timeline, // timeline_date is aliased as timeline in API now
      requirements: data.requirements ?? form.requirements,
      qualifications: data.qualifications ?? form.qualifications,
    })

    // ==========================
    // 2. Prefill editor safely
    // ==========================
    if (data.description) {
      const setEditorContent = () => {
        if (!editor.value) return
        try {
          const parsed =
            typeof data.description === 'string'
              ? JSON.parse(data.description)
              : data.description
          editor.value.commands.setContent(parsed)
        } catch {
          editor.value.commands.setContent(data.description)
        }
      }

      if (editor.value?.isDestroyed === false) {
        setEditorContent()
      } else {
        const stop = watch(editor, (ed) => {
          if (ed) {
            setEditorContent()
            stop()
          }
        })
      }
    }

    // ==========================
    // 3. Prefill selected project
    // ==========================
    if (data.project_id) {
      const applyProject = () => {
        const match = projectsStore.projects.find(
          (p) => p.id === data.project_id
        )
        if (match) {
          selectedProject.value = match
          form.project = match
          form.type = 'project'
          projectMode.value = 'existing'
          return true
        }
        return false
      }

      // Try immediately; if not found, wait for projects to load
      if (!applyProject()) {
        const stop = watch(
          () => projectsStore.projects,
          (list) => {
            if (list.length && applyProject()) stop()
          },
          { immediate: true }
        )
      }
    } else {
      // No project linked — clear it
      selectedProject.value = null
      form.project = null
    }
  },
  { immediate: true }
)

const { reference: selectProjectButtonRef, floating: selectProjectOptionsRef, floatingStyles: selectProjectStyles } = useDropdownFloating('bottom-start')

const { reference: projectLengthButtonRef, floating: projectLengthOptionsRef, floatingStyles: projectLengthStyles } = useDropdownFloating('top')

// ==============================
// 4. Steps (wizard support)
// ==============================
const currentStepIndex = ref(0)

const steps = computed<Step[]>(() => {
  const baseSteps: Step[] = [
    { key: 'basicInfo', label: 'Talent Pool Info', id: 1 },
    { key: 'jobDetails', label: 'Role & Requirements', id: 2 },
    { key: 'review', label: 'Review & Submit', id: 3 }
  ]

  if (form.type === 'project') {
    baseSteps.splice(1, 0, { key: 'projectDetails', label: 'Project Details', id: 0 })
  }

  // reassign ids cleanly
  return baseSteps.map((step, index) => ({ ...step, id: index + 1 }))
})

const currentStep = computed<Step>(() => {
  return steps.value[currentStepIndex.value] ?? steps.value[0] // fallback safety
})

const handleLocationUpdate = (newCoords: { lat: number; lng: number }) => {
  form.location.lat = newCoords.lat
  form.location.lng = newCoords.lng
  isLocationInvalid.value = false
}

const handleGeocodeFail = () => {
  form.location.lat = null
  form.location.lng = null
  isLocationInvalid.value = true
}

const increaseStep = () => {
  if (currentStepIndex.value < steps.value.length - 1) {
    currentStepIndex.value++
  }
}

const decreaseStep = () => {
  if (currentStepIndex.value > 0) {
    currentStepIndex.value--
  }
}

// ==============================
// Form validation
// ==============================
// Step validators
// Step 1 - Basic Info
const validateBasicInfo = () => {
  return (
    form.title.trim().length > 0 &&
    form.location.address.trim().length > 0 &&
    form.type !== null &&
    !isLocationInvalid.value
  )
}

// Step 2 - Job Details
const validateJobDetails = () => {
  const isDescriptionValid =
    !!editor.value && editor.value.getText().trim().length > 0

  return (
    isDescriptionValid &&
    form.workType !== null &&
    form.salary !== null &&
    form.hiringTimeline !== null &&
    (form.hiringTimeline !== 'specific_date' || form.timeline !== null)
  )
}

// Step 3 - Project Details
const validateProjectDetails = () => {
  if (projectMode.value === 'existing') {
    return selectedProject.value !== null
  }

  return (
    newProject.value.name.trim().length > 0 &&
    newProject.value.summary.trim().length > 0 &&
    newProject.value.startDate !== null &&
    newProject.value.length !== null
  )
}
// Step 4 - REview
const validateReview = () => true

// Step validators map
const stepValidators: Record<string, () => boolean> = {
  basicInfo: validateBasicInfo,
  jobDetails: validateJobDetails,
  projectDetails: validateProjectDetails,
  review: validateReview,
}

// Computed property
const canProceedToNextStep = computed(() => {
  const validator = stepValidators[currentStep.value.key]
  return validator ? validator() : false
})

// ==============================
// Requirements and Qualifications
// ==============================

const addRequirement = () => {
  const value = poolRequirement.value.trim()
  if (!value) return
  if (!form.requirements.includes(value)) {
    form.requirements.push(value)
  }
  poolRequirement.value = ''
}

const addQualification = () => {
  const value = poolQualification.value.trim()
  if (!value) return
  if (!form.qualifications.includes(value)) {
    form.qualifications.push(value)
  }
  poolQualification.value = ''
}

///////////////////////////
///////////////////////////
///////////////////////////
///////////////////////////
///////////////////////////
///////////////////////////
//IMPORTANT FOR LATER WHEN I NEED TO RESET - use newProject.value = createEmptyProject()
///////////////////////////
///////////////////////////
///////////////////////////
///////////////////////////

defineExpose({
  currentStepIndex,
  currentStep,
  steps,
  canProceedToNextStep,
  increaseStep,
  decreaseStep,
  form,
  newProject,
  projectMode,
  selectedProject,
  editor
})

onMounted(() => {
  projectsStore.fetchProjects()
});

// Keep date cleared when a non-date option is chosen
watch(
  () => form.hiringTimeline,
  (val) => {
    if (val && val !== 'specific_date') {
      form.timeline = null
    }
  }
)

</script>

<template>
  <div ref="contentAreaRef" class="p-6 flex-1 overflow-y-auto">
    <div v-show="currentStep.key === 'basicInfo'" class="space-y-6">
      <!-- Pool type -->
      <div class="space-y-1">
        <label
          for="poolType"
          class="flex items-center gap-2 leading-none text-[#0a0a0a] text-sm font-semibold select-none"
        >
          Talent Pool Type <span class="text-red-500">*</span>
        </label>

        <div class="grid grid-cols-2 gap-4">
          <button
            type="button"
            class="flex flex-col items-center justify-center gap-2 py-3 px-4 rounded-md border text-center bg-[#f5f5f5] text-[#737373] hover:bg-[#e5e5e5] focus:outline-none focus:ring-2 focus:ring-[#171717] transition-colors"
            :class="form.type === 'general' ? 'bg-[#e5e5e5] border-[#171717] text-[#171717]' : ''"
            @click="form.type = 'general'"
          >
            <User class="h-6 w-6" :class="form.type == 'general' ? 'text-[#171717]' : 'text-[#737373]'" />
            <span class="text-sm font-semibold">General Pool</span>
          </button>

          <button
            type="button"
            class="flex flex-col items-center justify-center gap-2 py-3 px-4 rounded-md border text-center bg-[#f5f5f5] text-[#737373] hover:bg-[#e5e5e5] focus:outline-none focus:ring-2 focus:ring-[#171717] transition-colors"
            :class="form.type === 'project' ? 'bg-[#e5e5e5] border-[#171717] text-[#171717]' : ''"
            @click="form.type = 'project'"
          >
            <Briefcase class="h-6 w-6" :class="form.type == 'project' ? 'text-[#171717]' : 'text-[#737373]'" />
            <span class="text-sm font-semibold">Project Pool</span>
          </button>
        </div>
      </div>

      <!-- Location -->
      <div class="space-y-1">
        <label
          for="poolLocation"
          class="flex items-center gap-2 leading-none text-[#0a0a0a] text-sm font-semibold select-none"
        >
          Location <span class="text-red-500">*</span>
        </label>
        <input
          v-model="form.location.address"
          type="text"
          id="poolLocation"
          name="poolLocation"
          class="
            w-full
            !bg-[#e5e5e54d]
            h-9
            min-w-0
            rounded-md
            text-[#0a0a0a]
            order
            px-3
            py-1
            placeholder:text-muted-foreground
            outline-none
            border
            !border-[#e5e5e5]
            md:text-sm"
        >
      </div>

      <GoogleMap v-if="form.location.address"
        editable 
        :address="form.location.address"
        @update:location="handleLocationUpdate"
        @geocode-failed="handleGeocodeFail"
      />

      <div class="space-y-1">
        <label
          for="poolName"
          class="
          flex 
          items-center
          gap-2
          leading-none 
          text-[#0a0a0a]
          text-sm
          font-semibold
          select-none
          group-data-[disabled=true]:pointer-events-none
          group-data-[disabled=true]:opacity-50
          peer-disabled:cursor-not-allowed
          peer-disabled:opacity-50"
        >
          Talent Pool Name <span class="text-red-500">*</span>
        </label>
        <input
          v-model="form.title"
          type="text"
          id="poolName"
          name="poolName"
          class="
          w-full
          !bg-[#e5e5e54d]
          h-9
          min-w-0
          rounded-md
          text-[#0a0a0a]
          order
          px-3
          py-1
          placeholder:text-muted-foreground
          outline-none
          border
          !border-[#e5e5e5]
          md:text-sm"
        />
      </div>
    </div>


    <div v-show="currentStep.key === 'jobDetails'" class="space-y-6">
      <div class="space-y-1">
        <label 
          class="flex items-center gap-2 leading-none text-[#0a0a0a] text-sm font-semibold select-none"
        >
          Job Details <span class="text-red-500">*</span>
        </label>
        
        <div v-if="editor" class="flex items-center gap-1 p-2 border !border-b-0 !border-[#e5e5e5] rounded-t-md bg-white">
          <button 
            type="button" 
            @click="editor.chain().focus().toggleBold().run()"
            class="p-2 rounded hover:bg-gray-200"
            :class="{ 'bg-gray-200 text-gray-900': editor.isActive('bold') }"
            title="Bold"
          >
            <Bold class="h-4 w-4" /> 
          </button>
          
          <button 
            type="button" 
            @click="editor.chain().focus().toggleItalic().run()"
            class="p-2 rounded hover:bg-gray-200"
            :class="{ 'bg-gray-200 text-gray-900': editor.isActive('italic') }"
            title="Italic"
          >
            <Italic class="h-4 w-4" />
          </button>

          <div class="w-px h-5 bg-gray-300 mx-1"></div>

          <button 
            type="button" 
            @click="editor.chain().focus().toggleBulletList().run()"
            class="p-2 rounded hover:bg-gray-200"
            :class="{ 'bg-gray-200 text-gray-900': editor.isActive('bulletList') }"
            title="Bulleted List"
          >
            <List class="h-4 w-4" />
          </button>
        </div>

        <EditorContent 
          :editor="editor" 
          class="border border-border rounded-b-md min-h-[150px]" 
        />
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">  
        <div class="space-y-1">
          <label class="flex items-center gap-2 leading-none text-[#0a0a0a] text-sm font-semibold select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50">
            Work type
          </label>
          <Listbox v-model="form.workType">
            <div class="relative">
              <ListboxButton class="relative w-full cursor-default rounded-md border border-[#e5e5e5] bg-[#e5e5e54d] h-9 px-3 pr-8 text-left text-sm font-medium text-[#0a0a0a] focus:outline-none focus:ring-2 focus:ring-blue-500">
                <span :class="!form.workType ? 'text-gray-400 italic' : 'text-[#0a0a0a]'" class="block truncate">
                  {{ workTypes.find(w => w.value === form.workType)?.label }}
                </span>
                <span class="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                  <ChevronDown class="h-4 w-4 text-gray-500" />
                </span>
              </ListboxButton>

              <Transition
                enter="transition ease-out duration-100"
                enter-from="opacity-0 scale-95"
                enter-to="opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leave-from="opacity-100 scale-100"
                leave-to="opacity-0 scale-95"
              >
                <ListboxOptions
                class="absolute z-50 mt-1 w-full overflow-auto rounded-md border border-gray-200 bg-white text-sm shadow-lg focus:outline-none list-none p-0 m-0"
                >
                  <ListboxOption disabled :value="null" class="hidden" />

                  <ListboxOption
                    v-for="workType in workTypes"
                    :key="workType.value"
                    :value="workType.value"
                    v-slot="{ selected, active }"
                    class="list-none" 
                  >
                    <div
                      class="flex items-center justify-between cursor-pointer px-3 py-1.5 text-sm transition-colors"
                      :class="[
                      active ? 'bg-gray-100' : '',
                      selected ? 'font-semibold text-[#171717]' : 'font-normal text-gray-700'
                      ]"
                      style="list-style: none;"
                    >
                      <span class="truncate">{{ workType.label }}</span>
                      <Check
                        v-if="selected"
                        class="h-3.5 w-3.5 text-blue-600"
                      />
                    </div>
                  </ListboxOption>
                </ListboxOptions>
              </Transition>
            </div>
          </Listbox>
        </div>

        <div class="space-y-1">
          <label class="flex items-center gap-2 leading-none text-[#0a0a0a] text-sm font-semibold select-none">
            Salary / Rate
          </label>

          <div class="flex items-center">
            <!-- Salary input -->
            <div class="relative flex-grow">
              <span class="absolute inset-y-0 left-3 flex items-center text-gray-500">$</span>
              <input 
                v-model="form.salary"
                type="number"
                placeholder="e.g., 90000"
                class="w-full !bg-[#e5e5e54d] h-9 rounded-l-md rounded-r-none pl-8 pr-3 border !border-[#e5e5e5] focus:ring-2 focus:ring-blue-500 focus:z-10"
              />
            </div>

            <!-- Dropdown for rate type -->
            <Listbox v-model="form.salaryType">
              <div class="relative">

                <ListboxButton class="relative w-full cursor-default rounded-r-md border border-[#e5e5e5] bg-[#e5e5e54d] h-9 px-3 pr-8 text-left text-sm font-medium text-[#0a0a0a] focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <span :class="!form.workType ? 'text-gray-400 italic' : 'text-[#0a0a0a]'" class="block truncate">
                    {{ salaryTypes.find(w => w.value === form.salaryType)?.label }}
                  </span>
                  <span class="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                    <ChevronDown class="h-4 w-4 text-gray-500" />
                  </span>
                </ListboxButton>

                <Transition
                  enter="transition ease-out duration-100"
                  enter-from="opacity-0 scale-95"
                  enter-to="opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leave-from="opacity-100 scale-100"
                  leave-to="opacity-0 scale-95"
                >
                  <ListboxOptions
                    class="absolute right-0 mt-1 w-28 rounded-md border border-[#e5e5e5] bg-white text-sm shadow-lg focus:outline-none z-10"
                  >
                    <ListboxOption
                      v-for="salaryType in salaryTypes"
                      :key="salaryType.value"
                      :value="salaryType.value"
                      v-slot="{ selected, active }: { selected: boolean; active: boolean }"
                    >
                    <div
                      class="cursor-pointer select-none px-3 py-1.5"
                      :class="[
                        active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                        selected ? 'font-semibold' : 'font-normal'
                      ]"
                    >
                      {{ salaryType.label }}
                    </div>
                    </ListboxOption>
                  </ListboxOptions>
                </Transition>
              </div>
            </Listbox>
          </div>
        </div>

      <div class="space-y-1 md:col-span-2">
          <label
            for="hiringTimeline"
            class="flex items-center gap-2 leading-none text-[#0a0a0a] text-sm font-semibold select-none"
          >
            Hiring timeline
          </label>

          <div class="space-y-3">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <button
                v-for="option in hiringTimelineOptions"
                :key="option.value"
                type="button"
                class="w-full rounded-md border px-3 py-2 text-left transition-colors"
                :class="form.hiringTimeline === option.value ? 'border-[#171717] bg-[#e5e5e5] text-[#171717]' : 'border-[#e5e5e5] bg-[#f5f5f5] text-[#0a0a0a]'"
                @click="form.hiringTimeline = option.value"
              >
                <span class="text-sm font-semibold">{{ option.label }}</span>
              </button>
            </div>

            <div v-if="form.hiringTimeline === 'specific_date'">
              <VueDatePicker
                v-model="form.timeline"
                :enable-time-picker="false"
                auto-apply
                teleport
                :min-date="new Date()"
                placeholder="Select hiring date"
                model-type="yyyy-MM-dd"
                :input-class="[
                'w-full h-9 rounded-md border border-[#e5e5e5] text-[#0a0a0a] px-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                'bg-[#f5f5f5]'
                ]"
                :menu-class="['rounded-lg shadow-lg border border-[#e5e5e5]', 'bg-[#f5f5e5]']"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="space-y-1">
        <label for="poolRequirments" class="flex items-center gap-2 leading-none text-[#0a0a0a] text-sm font-semibold select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50">
          Pre-employment requirements
        </label>

        <div class="flex flex-col space-y-2">
          <div class="flex space-x-2">
            <input v-model="poolRequirement" @keyup.enter="addRequirement" type="text" id="poolRequirments" name="poolRequirments" class="w-full !bg-[#e5e5e54d] text-[#0a0a0a] h-9 min-w-0 rounded-md order px-3 py-1 placeholder:text-muted-foreground outline-none border !border-[#e5e5e5] md:text-sm" placeholder="Add requirements"></input>
            <Button @click="addRequirement" class="!bg-[#e5e5e54d]" :variant="'outline'"><Plus class="h-6 w-6 text-[#737373]"/></Button>
          </div>
          <div class="flex flex-wrap gap-2 mt-2">
            <span
              v-for="(req, index) in form.requirements"
              :key="index"
              data-slot="badge"
              class="inline-flex items-center justify-center rounded-md border border-transparent !bg-[#e5e5e54d] text-secondary-foreground text-xs font-medium w-fit whitespace-nowrap shrink-0 gap-1 px-3 py-1 pr-1 transition-[color,box-shadow] hover:bg-secondary/90 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] overflow-hidden"
            >
              {{ req }}
              <button
                type="button"
                @click="form.requirements.splice(index, 1)"
                class="ml-2 hover:text-destructive flex items-center justify-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3 pointer-events-none" aria-hidden="true">
                  <path d="M18 6 6 18"></path>
                  <path d="m6 6 12 12"></path>
                </svg>
              </button>
            </span>
          </div>
        </div>
      </div>

      <div class="space-y-1">
        <label for="poolRequirments" class="flex items-center gap-2 leading-none text-[#0a0a0a] text-sm font-semibold select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50">
          Required Certifications & Qualifications
        </label>

        <div class="flex flex-col space-y-2">
          <div class="flex space-x-2">
            <input v-model="poolQualification" @keyup.enter="addQualification" type="text" id="poolQualifications" name="poolQualifications" class="w-full !bg-[#e5e5e54d] text-[#0a0a0a] h-9 min-w-0 rounded-md order px-3 py-1 placeholder:text-muted-foreground outline-none border !border-[#e5e5e5] md:text-sm" placeholder="Add certifications or qualifications"></input>
            <Button @click="addQualification" class="!bg-[#e5e5e54d]" :variant="'outline'"><Plus class="h-6 w-6 text-[#737373]"/></Button>
          </div>
          <div class="flex flex-wrap gap-2 mt-2">
            <span
              v-for="(req, index) in form.qualifications"
              :key="index"
              data-slot="badge"
              class="inline-flex items-center justify-center rounded-md border border-transparent !bg-[#e5e5e54d] text-secondary-foreground text-xs font-medium w-fit whitespace-nowrap shrink-0 gap-1 px-3 py-1 pr-1 transition-[color,box-shadow] hover:bg-secondary/90 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] overflow-hidden"
            >
              {{ req }}
              <button
                type="button"
                @click="form.qualifications.splice(index, 1)"
                class="ml-2 hover:text-destructive flex items-center justify-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3 pointer-events-none" aria-hidden="true">
                  <path d="M18 6 6 18"></path>
                  <path d="m6 6 12 12"></path>
                </svg>
              </button>
            </span>
          </div>
        </div>
      </div>
    </div>

    <div v-show="currentStep.key === 'projectDetails'" class="space-y-6">
      <div class="mb-6">
        <div class="flex items-center justify-center">
          <div class="flex bg-[#f5f5f5] rounded-lg p-1">
            <button
              @click="projectMode = 'existing'"
              :class="[
                'px-4 py-2 rounded-md text-sm font-medium transition-all duration-200',
                projectMode === 'existing' 
                  ? 'bg-[#171717] text-white shadow-sm' 
                  : 'text-[#737373] hover:text-[#171717]'
              ]"
            >
              <span class="flex items-center gap-2">
                <FolderOpen class="h-4 w-4" />
                Existing Project
              </span>
            </button>
            <button
              @click="projectMode = 'new'"
              :class="[
                'px-4 py-2 rounded-md text-sm font-medium transition-all duration-200',
                projectMode === 'new' 
                  ? 'bg-[#171717] text-white shadow-sm' 
                  : 'text-[#737373] hover:text-[#171717]'
              ]"
            >
              <span class="flex items-center gap-2">
                <Plus class="h-4 w-4" />
                New Project
              </span>
            </button>
          </div>
        </div>
      </div>

      <div class="min-h-[400px]">
        <div 
          v-show="projectMode === 'existing'" 
          class="bg-[#f5f5f5] rounded-lg p-4 border border-[#e5e5e5]"
        >
          <div class="flex items-center gap-3 mb-4">
            <FolderOpen class="h-5 w-5 text-[#737373]" />
            <div>
              <h3 class="font-semibold text-[#171717]">Select Existing Project</h3>
              <p class="text-sm text-[#737373]">Choose from your active projects</p>
            </div>
          </div>

          <Listbox v-if="projectsStore.projects.length > 0" v-model="selectedProject">
            <div class="relative">
              <ListboxButton ref="selectProjectButtonRef" class="relative w-full cursor-default rounded-md border border-[#e5e5e5] bg-[#e5e5e54d] h-9 px-3 pr-8 text-left text-sm font-medium text-[#0a0a0a] focus:outline-none focus:ring-2 focus:ring-[#171717]">
                <span v-if="selectedProject" class="flex items-center gap-2">
                  <span class="text-[#0a0a0a] font-medium">{{ selectedProject.name }}</span>
                  <span v-if="selectedProject.budget !== null" class="text-xs text-[#737373] ml-auto">${{ selectedProject.budget?.toLocaleString() }}</span>
                </span>
                <span v-else class="text-[#737373] italic flex items-center gap-2">
                  <Search class="h-4 w-4" />
                  Search and select a project...
                </span>
                <span class="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                  <ChevronDown class="h-4 w-4 text-[#737373]" />
                </span>
              </ListboxButton>

              <Transition
                enter="transition ease-out duration-100"
                enter-from="opacity-0 scale-95"
                enter-to="opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leave-from="opacity-100 scale-100"
                leave-to="opacity-0 scale-95"
              >
                <ListboxOptions ref="selectProjectOptionsRef" :style="selectProjectStyles" class="absolute z-[100] mt-1 overflow-auto rounded-md border border-[#e5e5e5] bg-white text-sm shadow-lg focus:outline-none list-none p-0 m-0 max-h-72">
                  <ListboxOption
                    v-for="project in projectsStore.projects"
                    :key="project.id"
                    :value="project"
                    v-slot="{ selected, active }"
                    class="list-none"
                  >
                    <div
                      :class="[
                        'flex items-center justify-between cursor-pointer px-3 py-2 text-sm transition-colors',
                        active ? 'bg-[#f5f5f5]' : '',
                        selected ? 'font-semibold text-[#171717] bg-[#e5e5e5]' : 'font-normal text-[#737373]'
                      ]"
                      style="list-style: none;"
                    >
                      <div class="flex items-center gap-2">
                          <div class="font-medium text-[#0a0a0a]">{{ project.name }}</div>
                      </div>
                      <div v-if="project.budget" class="flex items-center gap-2">
                        <span v-if="project.budget !== null" class="text-xs font-medium text-[#737373]">${{ project.budget?.toLocaleString() }}</span>
                        <Check v-if="selected" class="h-3.5 w-3.5 text-[#171717]" />
                      </div>
                    </div>
                  </ListboxOption>
                </ListboxOptions>
              </Transition>
            </div>
          </Listbox>
          <div v-else class="text-center py-6">
            <div class="mb-4 flex items-center justify-center">
              <div class="flex h-12 w-12 items-center justify-center rounded-full bg-[#e5e5e580]">
                <FolderOpen class="h-6 w-6 text-[#737373]" />
              </div>
            </div>
            <h3 class="font-semibold text-[#171717] mb-1">No Projects Found</h3>
            <p class="text-sm text-[#737373] mb-4 max-w-[200px] mx-auto">
              You don't have any projects to select from.
            </p>
            <Button
              :variant="'outline'"
              @click="projectMode = 'new'"
              class="w-full sm:w-auto"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 mr-2">
                <path d="M5 12h14" />
                <path d="M12 5v14" />
              </svg>
              Create a New Project
            </Button>
          </div>
        </div>

        <div v-show="projectMode === 'new'" class="space-y-4">
          <div class="space-y-1">
            <label class="flex items-center gap-2 text-sm font-semibold text-[#0a0a0a]">
              Project Name
              <span class="text-red-500">*</span>
            </label>
            <input
              v-model="newProject.name"
              type="text"
              class="w-full !bg-[#e5e5e54d] h-9 min-w-0 rounded-md text-[#0a0a0a] px-3 py-1 placeholder:text-muted-foreground outline-none border !border-[#e5e5e5] md:text-sm"
            />
          </div>

          <div class="space-y-1">
            <label class="flex items-center gap-2 text-sm font-semibold text-[#0a0a0a]">
              Project Summary
              <span class="text-red-500">*</span>
            </label>
            <textarea
              v-model="newProject.summary"
              rows="3"
              class="
                resize-none
                !border-[#e5e5e5]
                text-[#0a0a0a]
                placeholder:text-muted-foreground
                !bg-[#e5e5e54d]
                flex
                w-full
                rounded-md
                border
                px-3
                py-2
                text-base
                outline-none
                md:text-sm"
            ></textarea>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">  
            <div class="space-y-1">
              <label class="flex items-center gap-2 leading-none text-[#0a0a0a] text-sm font-semibold select-none">
                Budget
              </label>

              <div class="relative">
                <span class="absolute inset-y-0 left-3 flex items-center text-gray-500">$</span>
                <input
                  v-model.number="newProject.budget"
                  type="number"
                  min="0"
                  step="1000"
                  class="w-full !bg-[#e5e5e54d] h-9 rounded-md pl-8 pr-3 border !border-[#e5e5e5] focus:ring-2 focus:ring-blue-500 focus:z-10"
                />
              </div>
            </div>

            <div class="space-y-1">
              <label class="flex items-center gap-2 leading-none text-[#0a0a0a] text-sm font-semibold select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50">
                Expected Project Length
                <span class="text-red-500">*</span>
              </label>

              <Listbox v-model="newProject.length">
                <div class="relative">
                  <ListboxButton
                    ref="projectLengthButtonRef"
                    class="relative w-full cursor-default rounded-md border border-[#e5e5e5] bg-[#e5e5e54d] h-9 px-3 pr-8 text-left text-sm font-medium
                            text-[#0a0a0a] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <span :class="!newProject.length ? 'text-gray-400 italic' : 'text-[#0a0a0a]'" class="block truncate">
                      {{ newProject.length ?? 'Select length' }}
                    </span>
                    <span class="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                      <ChevronDown class="h-4 w-4 text-gray-500" />
                    </span>
                  </ListboxButton>
                  <Transition
                    enter="transition ease-out duration-100"
                    enter-from="opacity-0 scale-95"
                    enter-to="opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leave-from="opacity-100 scale-100"
                    leave-to="opacity-0 scale-95"
                  >
                    <ListboxOptions ref="projectLengthOptionsRef" :style="projectLengthStyles" class="absolute z-50 mt-1 w-full overflow-auto rounded-md border border-gray-200 bg-white text-sm shadow-lg focus:outline-none list-none p-0 m-0">
                      <ListboxOption disabled :value="null" class="hidden" />
                      <ListboxOption
                        v-for="value in projectDurations"
                        :key="value"
                        :value="value"
                        v-slot="{ selected, active }"
                        class="list-none" 
                      >
                        <div
                          class="flex items-center justify-between cursor-pointer px-3 py-1.5 text-sm transition-colors"
                          :class="[
                            active ? 'bg-gray-100' : '',
                            selected ? 'font-semibold text-[#171717]' : 'font-normal text-gray-700'
                          ]"
                          style="list-style: none;"
                        >
                          <span class="truncate">{{ value }}</span>
                          <Check
                            v-if="selected"
                            class="h-3.5 w-3.5 text-blue-600"
                          />
                        </div>
                      </ListboxOption>
                    </ListboxOptions>
                  </Transition>
                </div>
              </Listbox>
            </div>
          </div>

          <div class="space-y-1">
            <label
              for="hiringTimeline"
              class="flex items-center gap-2 leading-none text-[#0a0a0a] text-sm font-semibold select-none"
            >
              Expected Start Date
              <span class="text-red-500">*</span>
            </label>
            <VueDatePicker
              v-model="newProject.startDate"
              :enable-time-picker="false"
              auto-apply
              teleport
              :min-date="new Date()"
              placeholder="Select start date"
              model-type="yyyy-MM-dd"
              :input-class="[
              'w-full h-9 rounded-md border border-[#e5e5e5] text-[#0a0a0a] px-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
              'bg-[#f5f5f5]'
              ]"
              :menu-class="['rounded-lg shadow-lg border border-[#e5e5e5]', 'bg-[#f5f5e5]']"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-show="currentStep.key === 'review'" class="space-y-6">
      <div class="bg-[#f5f5f5] p-4 rounded-md space-y-3">
        <h4 class="font-semibold text-[#171717]">Basic Information</h4>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div class="text-xs text-[#737373] font-medium">Talent Pool Type</div>
            <div class="text-[#0a0a0a] capitalize">{{ form.type || '—' }}</div>
          </div>
          <div>
            <div class="text-xs text-[#737373] font-medium">Role Title</div>
            <div class="text-[#0a0a0a]">{{ form.title || '—' }}</div>
          </div>
          <div>
            <div class="text-xs text-[#737373] font-medium">Location</div>
            <div class="text-[#0a0a0a]">{{ form.location?.address || '—' }}</div>
          </div>
        </div>
      </div>

      <div class="bg-[#f5f5f5] p-4 rounded-md space-y-3">
        <h4 class="font-semibold text-[#171717]">Role & Requirements</h4>
        <div>
          <div class="text-xs text-[#737373] font-medium">Role Description</div>
          <div class="prose prose-sm max-w-none" v-html="editor?.getHTML()"></div>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div class="text-xs text-[#737373] font-medium">Work Type</div>
            <div class="text-[#0a0a0a]">{{ form.workType || '—' }}</div>
          </div>
          <div>
            <div class="text-xs text-[#737373] font-medium">Salary / Rate</div>
            <div class="text-[#0a0a0a]">${{ form.salary }} / {{ form.salaryType }}</div>
          </div>
          <div>
            <div class="text-xs text-[#737373] font-medium">Hiring Timeline</div>
            <div class="text-[#0a0a0a]">
              <span v-if="form.hiringTimeline === 'specific_date' && form.timeline">
                {{ new Date(form.timeline).toLocaleDateString() }}
              </span>
              <span v-else-if="form.hiringTimeline">
                {{ hiringTimelineOptions.find(o => o.value === form.hiringTimeline)?.label }}
              </span>
              <span v-else>—</span>
            </div>
          </div>
        </div>
        <div v-if="form.requirements.length > 0">
          <div class="text-xs text-[#737373] font-medium">Pre-employment Requirements</div>
          <ul class="flex flex-wrap gap-2 mt-1">
            <li v-for="req in form.requirements" :key="req" class="px-2 py-1 bg-[#e5e5e5] rounded-full text-xs text-[#0a0a0a] font-medium">{{ req }}</li>
          </ul>
        </div>
        <div v-if="form.qualifications.length > 0">
          <div class="text-xs text-[#737373] font-medium">Certifications & Qualifications</div>
          <ul class="flex flex-wrap gap-2 mt-1">
            <li v-for="qual in form.qualifications" :key="qual" class="px-2 py-1 bg-[#e5e5e5] rounded-full text-xs text-[#0a0a0a] font-medium">{{ qual }}</li>
          </ul>
        </div>
      </div>

      <div v-if="form.type === 'project'" class="bg-[#f5f5f5] p-4 rounded-md space-y-3">
        <h4 class="font-semibold text-[#171717]">Project Details</h4>
        <div v-if="projectMode === 'existing' && selectedProject">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div class="text-xs text-[#737373] font-medium">Project Name</div>
              <div class="text-[#0a0a0a]">{{ selectedProject.name }}</div>
            </div>
            <div>
              <div class="text-xs text-[#737373] font-medium">Location</div>
            </div>
          </div>
          <div>
            <div class="text-xs text-[#737373] font-medium mt-2">Budget</div>
            <div class="text-[#0a0a0a]">${{ selectedProject.budget?.toLocaleString() }}</div>
          </div>
        </div>
        <div v-else-if="projectMode === 'new'">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div class="text-xs text-[#737373] font-medium">Project Name</div>
              <div class="text-[#0a0a0a]">{{ newProject.name || '—' }}</div>
            </div>
          </div>
          <div>
            <div class="text-xs text-[#737373] font-medium">Project Summary</div>
            <div class="text-[#0a0a0a] whitespace-pre-line">{{ newProject.summary || '—' }}</div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div class="text-xs text-[#737373] font-medium">Budget</div>
              <div class="text-[#0a0a0a]">{{ newProject.budget ? '$' + newProject.budget.toLocaleString() : '—' }}</div>
            </div>
            <div>
              <div class="text-xs text-[#737373] font-medium">Expected Project Length</div>
              <div class="text-[#0a0a0a]">{{ newProject.length || '—' }}</div>
            </div>
          </div>
          <div>
            <div class="text-xs text-[#737373] font-medium">Expected Start Date</div>
            <div class="text-[#0a0a0a]">{{ newProject.startDate ? new Date(newProject.startDate).toLocaleDateString() : '—' }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.input-with-icon {
  padding-left: 1.75rem !important; /* same as pl-7 */
}

:deep(.dp__theme_light) {
  --dp-background-color: #f5f5f5;
  --dp-text-color: #0a0a0a;
  --dp-hover-color: #e5e5e5;
  --dp-primary-color: #3b82f6; /* Tailwind blue-500 */
}
/* Target the Tiptap editor content */
.ProseMirror {
  /* Remove the default browser outline on focus */
  outline: none;
}

/* Set default styles for paragraphs within the editor */
.ProseMirror p {
  margin: 0; /* Ensures clean line breaks */
}

/* Style for the active toolbar button */
.is-active {
  background-color: #e5e5e5;
}
</style>
